import { Name } from 'sr-types/lib/account/v1/graphql';
import { ReferenceInput } from 'sr-types/lib/order/v1/graphql';
import { ContactInput, NameInput } from 'sr-types/lib/organization/v1/graphql';
import { extractNames } from '../../person/utils/extractNames';
import { DEFAULT_PERSON_LAST_NAME } from '../../person/utils/personConstants';
import { getContactNameFromObject } from '../utils/nameUtils';

export interface RawContact {
    label?: string;
    ref?: ReferenceInput | null;
    id?: string | null;
    name?: string | Name;
    [key: string]: any;
}

/**
 * Normalizes a contact object into a structured input format.
 *
 * - If neither `name` nor `label` is provided, the function returns `null`.
 * - If a name/label is a string, it will be processed through `extractNames`.
 * - If it is already an object (e.g., organization-derived contact), it is used as-is.
 *
 * This is the conversion behind every contact the app sends: the order editor's own contacts,
 * the approval workflow's approvers and notify list, and the IP Deal request's deal team. It
 * lives here rather than beside any one of them so that importing it costs nothing but the two
 * leaf modules below - `resourceOrderEditor` re-exports it for the callers that always had it.
 *
 * @param contact - The raw contact object that may contain name, label, reference, and other metadata.
 * @returns A normalized contact input object, or `null` if no name/label is available.
 */
export const getContactInput = (contact: RawContact | null | undefined): ContactInput | null => {
    const { label, ref, id, name, ...contactInput } = contact || {};

    if (!name && !label) return null;

    const rawName = name || label;

    // `Name` declares both parts optional and `NameInput` requires them, so an object contact
    // is filled out the same way `extractNames` fills out a string one.
    const nameInput: NameInput =
        typeof rawName === 'string'
            ? extractNames(rawName, DEFAULT_PERSON_LAST_NAME)
            : { ...rawName, firstName: rawName.firstName ?? '', lastName: rawName.lastName ?? '' };

    return {
        ...(contactInput ?? {}),
        name: nameInput,
        personReference: ref
    };
};

export const getRawContactFromOrg = (contact: ContactInput | null | undefined): RawContact | null => {
    if (!contact) return null;

    return {
        ...contact,
        label: getContactNameFromObject(contact.name),
        ref: {
            ...contact.personReference,
            label: getContactNameFromObject(contact.name)
        }
    };
};
